import Dexie, { Table } from 'dexie';

// Database interfaces
export interface Patient {
  id: string;
  name: string;
  priority: 'High' | 'Medium' | 'Low';
  points: number;
  lastUpdated?: number;
  syncStatus?: 'synced' | 'pending' | 'error';
}

export interface Alert {
  id: string;
  title: string;
  patient: string;
  severity?: 'critical' | 'high' | 'medium' | 'low';
  timestamp?: number;
  acknowledged?: boolean;
  lastUpdated?: number;
  syncStatus?: 'synced' | 'pending' | 'error';
}

export interface SyncQueue {
  id?: number;
  action: 'create' | 'update' | 'delete';
  entity: 'patient' | 'alert' | 'handoff';
  entityId: string;
  data: any;
  timestamp: number;
  retries: number;
  error?: string;
}

// Database schema
export class RohimayaDB extends Dexie {
  patients!: Table<Patient, string>;
  alerts!: Table<Alert, string>;
  syncQueue!: Table<SyncQueue, number>;

  constructor() {
    super('RohimayaDB');
    
    this.version(1).stores({
      patients: 'id, priority, lastUpdated, syncStatus',
      alerts: 'id, patient, severity, timestamp, acknowledged, syncStatus',
      syncQueue: '++id, action, entity, timestamp, retries',
    });
  }
}

// Create database instance
export const db = new RohimayaDB();

// Helper functions
export async function savePatients(patients: Patient[]) {
  const patientsWithTimestamp = patients.map(p => ({
    ...p,
    lastUpdated: Date.now(),
    syncStatus: 'synced' as const,
  }));
  await db.patients.bulkPut(patientsWithTimestamp);
}

export async function saveAlerts(alerts: Alert[]) {
  const alertsWithTimestamp = alerts.map(a => ({
    ...a,
    lastUpdated: Date.now(),
    syncStatus: 'synced' as const,
  }));
  await db.alerts.bulkPut(alertsWithTimestamp);
}

export async function queueSync(
  action: SyncQueue['action'],
  entity: SyncQueue['entity'],
  entityId: string,
  data: any
) {
  await db.syncQueue.add({
    action,
    entity,
    entityId,
    data,
    timestamp: Date.now(),
    retries: 0,
  });
}

export async function getPendingSyncs(): Promise<SyncQueue[]> {
  return await db.syncQueue.where('retries').below(5).toArray();
}

export async function markSyncComplete(id: number) {
  await db.syncQueue.delete(id);
}

export async function markSyncFailed(id: number, error: string) {
  const item = await db.syncQueue.get(id);
  if (item) {
    await db.syncQueue.update(id, {
      retries: item.retries + 1,
      error,
    });
  }
}

// Clear all data (for testing or logout)
export async function clearOfflineData() {
  await db.patients.clear();
  await db.alerts.clear();
  await db.syncQueue.clear();
}
